import { useMemo, useState } from 'react';
import { usePermission } from '../hooks/usePermission';
import { formatInboundPayload } from '../utils/formatPayload';

const REDACTED = 'REDACTED';

const sensitiveKeyFragments = [
  'token',
  'accesstoken',
  'refreshtoken',
  'authorization',
  'bearer',
  'apikey',
  'key',
  'secret',
  'password',
  'passwd',
  'credential',
  'certificate',
  'cert',
  'privatekey',
  'publickey',
  'signature',
  'digest',
  'cookie',
  'session',
  'as2',
  'partnersecret',
  'clientsecret',
  'security',
  'auth',
  // Individual identity fields found in customs/maritime canonical payloads (CUSDEC ML
  // declarant/soumissionnaire/agentCote, Tryptique conducteurs, the legacy DUM parser's
  // agentCoteNom/agentCoteEmail) — a person's name, email, or ID-document number, as opposed
  // to a business/company code or reference number (see businessIdentifierFragments below).
  'name',
  'nom',
  'mail',
  'identifiant',
];

const businessIdentifierFragments = [
  'containernumber',
  'containerreference',
  'declarationnumber',
  'declarationreference',
  'blnumber',
  'billoflading',
  'ice',
  'identifier',
];

export function PayloadViewer({
  value,
  contentType,
  filename = 'payload.txt',
  className = 'code-block content-viewer',
}: {
  value: unknown;
  contentType?: string;
  filename?: string;
  className?: string;
}) {
  const canRevealSensitive = usePermission('integration.payload.revealSensitive');
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const visibleMode = canRevealSensitive && revealed ? 'raw' : 'redacted';
  const rendered = useMemo(() => renderPayload(value, contentType, visibleMode), [contentType, value, visibleMode]);

  async function copyVisiblePayload() {
    if (!navigator.clipboard) return;
    await navigator.clipboard.writeText(rendered);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }

  function downloadVisiblePayload() {
    const blob = new Blob([rendered], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = visibleMode === 'redacted' ? withRedactedSuffix(filename) : filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="payload-viewer">
      <div className="payload-viewer-toolbar">
        <span className={`risk-pill risk-${visibleMode === 'raw' ? 'high' : 'medium'}`}>
          {visibleMode === 'raw' ? 'Raw sensitive view' : 'Redacted'}
        </span>
        <button type="button" className="ghost-button" onClick={() => void copyVisiblePayload()}>
          {copied ? 'Copied' : 'Copy visible'}
        </button>
        <button type="button" className="ghost-button" onClick={downloadVisiblePayload}>
          Download visible
        </button>
        {canRevealSensitive ? (
          <button
            type="button"
            className={revealed ? 'danger-button' : 'ghost-button'}
            onClick={() => {
              // TODO audit integration: emit a frontend/backend audit event when raw payload reveal is enabled.
              setRevealed((current) => !current);
            }}
          >
            {revealed ? 'Hide raw' : 'Reveal raw'}
          </button>
        ) : null}
      </div>
      <pre className={className}>{rendered}</pre>
    </div>
  );
}

export function renderPayload(value: unknown, contentType: string | undefined, mode: 'redacted' | 'raw' = 'redacted'): string {
  const visibleValue = mode === 'raw' ? value : redactSensitiveValue(value);
  if (typeof visibleValue === 'string') {
    return formatInboundPayload(visibleValue, contentType);
  }
  return JSON.stringify(visibleValue, null, 2);
}

export function redactSensitiveValue(value: unknown, keyHint = ''): unknown {
  if (value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveValue(item, keyHint));
  }

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        redactEntry(key, entry),
      ]),
    );
  }

  if (typeof value === 'string') {
    return redactStringValue(value, keyHint);
  }

  return value;
}

function redactEntry(key: string, value: unknown): unknown {
  const normalizedKey = normalizeKey(key);
  if (isSensitiveKey(normalizedKey)) return REDACTED;
  if (typeof value === 'string' && isBusinessIdentifierKey(normalizedKey)) return maskBusinessIdentifier(value);
  return redactSensitiveValue(value, key);
}

function redactStringValue(value: string, keyHint = ''): string {
  if (!value) return value;
  const normalizedKey = normalizeKey(keyHint);
  if (isSensitiveKey(normalizedKey)) return REDACTED;
  if (isBusinessIdentifierKey(normalizedKey)) return maskBusinessIdentifier(value);

  let redacted = value
    .replace(/-----BEGIN [^-]+-----[\s\S]*?-----END [^-]+-----/gi, REDACTED)
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, `Bearer ${REDACTED}`)
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, REDACTED)
    .replace(/(<(?:[^>]*:)?(?:token|accessToken|refreshToken|authorization|apiKey|secret|password|passwd|credential|certificate|cert|privateKey|publicKey|signature|digest|cookie|session|as2|security|auth)[^>]*>)([\s\S]*?)(<\/(?:[^>]*:)?(?:token|accessToken|refreshToken|authorization|apiKey|secret|password|passwd|credential|certificate|cert|privateKey|publicKey|signature|digest|cookie|session|as2|security|auth)>)/gi, `$1${REDACTED}$3`)
    .replace(/\b(token|accessToken|refreshToken|authorization|apiKey|secret|password|passwd|credential|certificate|cert|privateKey|publicKey|signature|digest|cookie|session|as2|security|auth)\s*[:=]\s*["']?[^"',\s<]+/gi, `$1=${REDACTED}`);

  if (looksLikeHighEntropySecret(redacted)) {
    redacted = REDACTED;
  }

  return redacted;
}

function isSensitiveKey(normalizedKey: string): boolean {
  return sensitiveKeyFragments.some((fragment) => normalizedKey.includes(fragment));
}

function isBusinessIdentifierKey(normalizedKey: string): boolean {
  return businessIdentifierFragments.some((fragment) => normalizedKey.includes(fragment));
}

function normalizeKey(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

function maskBusinessIdentifier(value: string): string {
  const normalized = value.trim();
  if (normalized.length <= 6) return normalized.length > 0 ? '***' : normalized;
  return `${normalized.slice(0, 3)}***${normalized.slice(-3)}`;
}

function looksLikeHighEntropySecret(value: string): boolean {
  const normalized = value.trim();
  if (normalized.length < 32 || /\s/.test(normalized)) return false;
  const hasLower = /[a-z]/.test(normalized);
  const hasUpper = /[A-Z]/.test(normalized);
  const hasDigit = /\d/.test(normalized);
  const hasSymbol = /[+/=_-]/.test(normalized);
  return [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length >= 3;
}

function withRedactedSuffix(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot <= 0) return `${filename}-redacted`;
  return `${filename.slice(0, dot)}-redacted${filename.slice(dot)}`;
}
